import { useCallback, useEffect, useState } from "react";
import { Bell, CheckCheck, Info, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";

const ICONS = { info: Info, success: CheckCircle2, warning: AlertTriangle };

export default function Notifications() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/notifications").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    setItems((it) => it.map((n) => (n.notif_id === id ? { ...n, read: true } : n)));
    try { await api.post(`/notifications/${id}/read`); } catch { load(); }
  };
  const markAll = async () => {
    setItems((it) => it.map((n) => ({ ...n, read: true })));
    try { await api.post("/notifications/read-all"); } catch { load(); }
  };

  return (
    <div className="fv-fade-up max-w-3xl">
      <div className="flex items-start justify-between mb-8">
        <PageHeader eyebrow="Account" title="Notifications" subtitle="Stay up to date with everything happening in your account." />
        <Button onClick={markAll} data-testid="mark-all-read-btn" variant="outline"
          className="border-white/15 bg-transparent text-white hover:bg-white/5 rounded-full shrink-0">
          <CheckCheck className="w-4 h-4 mr-2" /> Mark all read
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20 rounded-2xl border border-white/8 bg-[#121214]">
          <Bell className="w-10 h-10 text-[#52525B] mx-auto mb-3" />
          <p className="text-[#71717A]">You're all caught up.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="notifications-list">
          {items.map((n) => {
            const Icon = ICONS[n.kind] || Info;
            return (
              <div key={n.notif_id} onClick={() => !n.read && markRead(n.notif_id)} data-testid="notification-item"
                className={`flex gap-4 rounded-xl border p-4 cursor-pointer transition-colors ${
                  n.read ? "border-white/8 bg-[#0f0f11]" : "border-[#D4AF37]/25 bg-[#151310] hover:bg-[#1a1712]"}`}>
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">{n.title}</span>
                    {!n.read && <span className="w-2 h-2 rounded-full fv-gold-gradient" />}
                  </div>
                  <p className="text-sm text-[#A1A1AA] mt-0.5">{n.message}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
